/**
 * Mobile sticky product bar (snippets/sticky.liquid).
 * Visible on product pages below the theme's mobile breakpoint (749px) while the main
 * add-to-bag button is scrolled out of view.
 */
class ProductStickyPanel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #mediaQuery = window.matchMedia('(max-width: 749px)');

  isVisible = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#mediaQuery.addEventListener('change', () => this.checkProductPage(), { signal });
    window.addEventListener('popstate', () => this.checkProductPage(), { signal });

    this.checkProductPage();
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  get isMobile() {
    return this.#mediaQuery.matches;
  }

  isOnProductPage() {
    return /\/products\//.test(window.location.pathname);
  }

  shouldBeVisible() {
    return this.isOnProductPage() && this.isMobile;
  }

  checkProductPage() {
    const shouldShow = this.shouldBeVisible();
    if (shouldShow && !this.isVisible) this.showPanel();
    else if (!shouldShow && this.isVisible) this.hidePanel();
  }

  showPanel() {
    this.style.display = 'block';
    this.isVisible = true;
    this.dispatchEvent(new CustomEvent('sticky-panel-shown', { bubbles: true, detail: { panel: this } }));
  }

  hidePanel() {
    this.style.display = 'none';
    this.isVisible = false;
    this.dispatchEvent(new CustomEvent('sticky-panel-hidden', { bubbles: true, detail: { panel: this } }));
  }

  setupIntersectionObserver() {
    const target =
      document.querySelector('.product-details add-to-cart-component') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('.product-information__media');

    if (!target) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.classList.toggle('main-content-visible', entry.isIntersecting);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );
    this.#observer.observe(target);
  }

  /** @param {boolean} show */
  toggle(show) {
    if (show) this.showPanel();
    else this.hidePanel();
  }
}

if (!customElements.get('product-sticky-panel')) {
  customElements.define('product-sticky-panel', ProductStickyPanel);
}
